import React from 'react';
import FileUploadSimple from '@ajat/design-system/FileUpload/Simple';
import { ComponentPage, DemoCard } from '../shared';

const uploadSucceeds = () => new Promise((resolve) => setTimeout(resolve, 2000));
const uploadFails = () =>
  new Promise((_, reject) => setTimeout(() => reject(new Error('Server error: 500')), 1500));

export const meta = {
  component: FileUploadSimple,
  componentName: 'FileUploadSimple',
  title: 'File Upload',
  group: 'Forms',
  importPath: '@ajat/design-system/FileUpload/Simple',
  description:
    'Single-file picker that uploads as soon as a file is chosen. Five states — idle, loading, uploaded, error, disabled — with a progress bar clipped to the bottom edge of the field. Error and helper text are absolutely positioned, so neither changes the height of the control.',
  props: [
    ['label', 'string', "'Choose file'", 'Floating label / placeholder text'],
    ['helperText', 'string', "'8MB, 3000x3000px, PDF, JPG, PNF'", 'Right-aligned hint below the field'],
    ['accept', 'string', '—', 'Native accept attribute, e.g. ".pdf,.jpg,.png"'],
    ['error', 'string', "''", 'External error message — forces the error state'],
    ['disabled', 'bool', 'false', 'Disables all interaction'],
    ['onUpload', 'func', '—', 'Receives the File and returns a Promise. Resolve → uploaded, reject → error. Omit it and the upload is simulated.'],
    ['onClear', 'func', '—', 'Called when the user removes the file with the X button'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  propsNote:
    'This component declares no propTypes, so the props-drift test cannot verify this table — keep it in step with the component by hand.',
  usage: [
    {
      title: 'With a real upload',
      code: `import FileUploadSimple from '@ajat/design-system/FileUpload/Simple';

<FileUploadSimple
  label="Upload tegning — forsiden"
  helperText="8MB, PDF, JPG, PNG"
  accept=".pdf,.jpg,.jpeg,.png"
  onUpload={async (file) => {
    const body = new FormData();
    body.append('file', file);
    const res = await fetch('/api/uploads', { method: 'POST', body });
    if (!res.ok) throw new Error('Upload failed');   // → error state
    setArtwork(await res.json());                    // → uploaded state
  }}
  onClear={() => setArtwork(null)}
/>`,
    },
  ],
};

export default function FileUploadPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Idle" hint="pick a file — the upload starts immediately">
        <div className="max-w-md">
          <FileUploadSimple label="Choose file" onUpload={uploadSucceeds} />
        </div>
      </DemoCard>

      <DemoCard title="Success path" hint="resolves after 2s">
        <div className="max-w-md">
          <FileUploadSimple
            label="Upload image"
            helperText="8MB, JPG, PNG"
            accept=".jpg,.jpeg,.png"
            onUpload={uploadSucceeds}
          />
        </div>
      </DemoCard>

      <DemoCard title="Failure path" hint="rejects after 1.5s">
        <div className="max-w-md">
          <FileUploadSimple label="Upload document" accept=".pdf" onUpload={uploadFails} />
        </div>
      </DemoCard>

      <DemoCard title="External error">
        <div className="max-w-md">
          <FileUploadSimple label="Upload document" error="The file format is not correct" />
        </div>
      </DemoCard>

      <DemoCard title="Disabled">
        <div className="max-w-md">
          <FileUploadSimple label="Upload" disabled />
        </div>
      </DemoCard>

      <DemoCard title="Custom label and helper text" hint="accept narrows the file picker">
        <div className="max-w-md flex flex-col gap-[var(--space-ui-medium-m)]">
          <FileUploadSimple
            label="Upload logo"
            helperText="SVG or PNG, max 2MB"
            accept=".svg,.png"
            onUpload={uploadSucceeds}
          />
          <FileUploadSimple
            label="Attach document"
            helperText="PDF only, max 10MB"
            accept=".pdf"
            onUpload={uploadSucceeds}
          />
        </div>
      </DemoCard>

      <DemoCard title="State machine" surface={false}>
        <div className="flex flex-wrap items-center gap-[var(--space-ui-medium-s)]">
          {['idle', 'loading', 'uploaded'].map((state, i, arr) => (
            <React.Fragment key={state}>
              <span className="typo-btn-tertiary px-[var(--space-ui-small-l)] py-[var(--space-ui-small-m)] rounded-[var(--radius-button)] bg-[var(--color-gray-10)] text-[var(--color-gray-0)]">
                {state}
              </span>
              {i < arr.length - 1 && <span className="typo-body-sm text-[var(--color-gray-4)]">→</span>}
            </React.Fragment>
          ))}
          <span className="typo-body-sm text-[var(--color-gray-4)]">·</span>
          <span className="typo-btn-tertiary px-[var(--space-ui-small-l)] py-[var(--space-ui-small-m)] rounded-[var(--radius-button)] bg-[var(--color-ui-error-background)] text-[var(--color-ui-error)]">
            error
          </span>
          <span className="typo-body-sm text-[var(--color-gray-6)]">on reject — the Upload icon retries</span>
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
